import React from 'react';

import { cardById } from '../deck';

import { GOLD, GOLD_SOFT, MajorSigil, starPoints, SuitGlyph } from './glyphs';

const ROMAN = [
    '0',
    'I',
    'II',
    'III',
    'IV',
    'V',
    'VI',
    'VII',
    'VIII',
    'IX',
    'X',
    'XI',
    'XII',
    'XIII',
    'XIV',
    'XV',
    'XVI',
    'XVII',
    'XVIII',
    'XIX',
    'XX',
    'XXI',
];
const RANK_MARK = [
    'A',
    '2',
    '3',
    '4',
    '5',
    '6',
    '7',
    '8',
    '9',
    '10',
    'P',
    'Kn',
    'Q',
    'K',
];

/** Where the pips of a numbered minor card go, as fractions of the art area (x in ±1, y in ±1). */
const PIPS: Record<number, [number, number][]> = {
    1: [[0, 0]],
    2: [
        [0, -0.6],
        [0, 0.6],
    ],
    3: [
        [0, -0.7],
        [0, 0],
        [0, 0.7],
    ],
    4: [
        [-0.55, -0.6],
        [0.55, -0.6],
        [-0.55, 0.6],
        [0.55, 0.6],
    ],
    5: [
        [-0.55, -0.6],
        [0.55, -0.6],
        [0, 0],
        [-0.55, 0.6],
        [0.55, 0.6],
    ],
    6: [
        [-0.55, -0.7],
        [0.55, -0.7],
        [-0.55, 0],
        [0.55, 0],
        [-0.55, 0.7],
        [0.55, 0.7],
    ],
    7: [
        [-0.55, -0.7],
        [0.55, -0.7],
        [0, -0.35],
        [-0.55, 0],
        [0.55, 0],
        [-0.55, 0.7],
        [0.55, 0.7],
    ],
    8: [
        [-0.55, -0.7],
        [0.55, -0.7],
        [0, -0.35],
        [-0.55, 0],
        [0.55, 0],
        [0, 0.35],
        [-0.55, 0.7],
        [0.55, 0.7],
    ],
    9: [
        [-0.55, -0.75],
        [0.55, -0.75],
        [-0.55, -0.25],
        [0.55, -0.25],
        [0, 0],
        [-0.55, 0.25],
        [0.55, 0.25],
        [-0.55, 0.75],
        [0.55, 0.75],
    ],
    10: [
        [-0.55, -0.75],
        [0.55, -0.75],
        [0, -0.5],
        [-0.55, -0.25],
        [0.55, -0.25],
        [-0.55, 0.25],
        [0.55, 0.25],
        [0, 0.5],
        [-0.55, 0.75],
        [0.55, 0.75],
    ],
};

/** Decorative frame shared by the face and the back: a double gold rule with a diamond in each corner. */
const Frame: React.FC = () => (
    <g fill="none" stroke={GOLD} strokeLinecap="round" strokeLinejoin="round">
        <rect x="6" y="6" width="188" height="328" rx="12" strokeWidth="1.6" />
        <rect
            x="12"
            y="12"
            width="176"
            height="316"
            rx="8"
            strokeWidth="0.7"
            opacity="0.7"
        />
        {[
            [20, 20],
            [180, 20],
            [20, 320],
            [180, 320],
        ].map(([x, y]) => (
            <polygon
                key={`${x}${y}`}
                points={starPoints(4, 5, 1.6, x, y)}
                fill={GOLD}
                stroke="none"
            />
        ))}
    </g>
);

const Defs: React.FC = () => (
    <defs>
        <linearGradient id="arcana-card-bg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#241540" />
            <stop offset="1" stopColor="#0c0717" />
        </linearGradient>
        <radialGradient id="arcana-card-glow" cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor="#7a4bd0" stopOpacity="0.45" />
            <stop offset="1" stopColor="#7a4bd0" stopOpacity="0" />
        </radialGradient>
    </defs>
);

const nameSize = (name: string) =>
    name.length > 22 ? 9.5 : name.length > 17 ? 11 : 13;

interface FaceProps {
    cardId: string;
    name: string;
    reversed?: boolean;
}

/** A card face, drawn from the card's id alone: gold line art on a night-violet ground. Reversed cards turn the art, not the name. */
export const CardFace: React.FC<FaceProps> = ({ cardId, name, reversed }) => {
    const card = cardById(cardId);
    if (!card) return null;
    const major = card.arcana === 'major';
    const court = !major && card.rank > 10;
    const mark = major ? ROMAN[card.rank] : RANK_MARK[card.rank - 1];

    return (
        <svg
            viewBox="0 0 200 340"
            role="img"
            aria-label={name}
            style={{ display: 'block', width: '100%', height: '100%' }}
        >
            <Defs />
            <rect
                width="200"
                height="340"
                rx="14"
                fill="url(#arcana-card-bg)"
            />
            <circle cx="100" cy="168" r="92" fill="url(#arcana-card-glow)" />
            <Frame />
            <text
                x="100"
                y="44"
                textAnchor="middle"
                fontFamily="Cinzel, serif"
                fontSize="17"
                letterSpacing="3"
                fill={GOLD}
            >
                {mark}
            </text>
            <g transform={`translate(100 168) rotate(${reversed ? 180 : 0})`}>
                <circle
                    r="82"
                    fill="none"
                    stroke={GOLD}
                    strokeWidth="0.6"
                    opacity="0.5"
                />
                <circle
                    r="76"
                    fill="none"
                    stroke={GOLD}
                    strokeWidth="0.4"
                    strokeDasharray="1 5"
                    opacity="0.7"
                />
                <g
                    fill="none"
                    stroke={GOLD}
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    transform={major ? 'scale(1.45)' : undefined}
                >
                    {major ? (
                        <MajorSigil rank={card.rank} />
                    ) : court ? (
                        <g transform="scale(2)">
                            <SuitGlyph suit={card.suit!} />
                            <path
                                d="M-12 -34 L-8 -42 L-4 -36 L0 -44 L4 -36 L8 -42 L12 -34Z"
                                fill={GOLD_SOFT}
                                strokeWidth="1.2"
                            />
                        </g>
                    ) : (
                        PIPS[card.rank].map(([x, y], i) => (
                            <g
                                key={i}
                                transform={`translate(${x * 48} ${y * 82}) scale(${card.rank === 1 ? 2.4 : 0.8})`}
                            >
                                <SuitGlyph suit={card.suit!} />
                            </g>
                        ))
                    )}
                </g>
            </g>
            <text
                x="100"
                y="305"
                textAnchor="middle"
                fontFamily="Cinzel, serif"
                fontSize={nameSize(name)}
                letterSpacing="1.5"
                fill={GOLD}
            >
                {name.toUpperCase()}
            </text>
        </svg>
    );
};

/** The back of every card: an eye inside a twelve-pointed star, ringed by moons. */
export const CardBack: React.FC = () => (
    <svg
        viewBox="0 0 200 340"
        aria-hidden="true"
        style={{ display: 'block', width: '100%', height: '100%' }}
    >
        <Defs />
        <rect width="200" height="340" rx="14" fill="url(#arcana-card-bg)" />
        <Frame />
        <g
            transform="translate(100 170)"
            fill="none"
            stroke={GOLD}
            strokeLinecap="round"
            strokeLinejoin="round"
        >
            <circle r="70" strokeWidth="0.8" />
            <circle r="62" strokeWidth="0.4" strokeDasharray="1 4" />
            <polygon
                points={starPoints(12, 58, 30)}
                strokeWidth="1.2"
                fill={GOLD_SOFT}
            />
            <circle r="26" strokeWidth="1" />
            <path d="M-20 0 Q0 -16 20 0 Q0 16 -20 0Z" strokeWidth="1.4" />
            <circle r="5.5" fill={GOLD} stroke="none" />
            {Array.from({ length: 12 }, (_, i) => {
                const a = (i * Math.PI) / 6;
                return (
                    <circle
                        key={i}
                        cx={Math.cos(a) * 78}
                        cy={Math.sin(a) * 78}
                        r="1.6"
                        fill={GOLD}
                        stroke="none"
                    />
                );
            })}
            <path
                d="M3 -9 A9 9 0 1 0 3 9 A7 7 0 1 1 3 -9Z"
                fill={GOLD}
                stroke="none"
                transform="translate(0 -104) rotate(-90)"
            />
            <path
                d="M3 -9 A9 9 0 1 0 3 9 A7 7 0 1 1 3 -9Z"
                fill={GOLD}
                stroke="none"
                transform="translate(0 104) rotate(90)"
            />
        </g>
    </svg>
);
